
import React, { useState, useEffect, useMemo } from 'react';
import { Globe, Globe2, ChevronRight, Factory, MapPin, ChevronLeft, Upload, Loader2, Link as LinkIcon, X, Star, Quote, Plus, Trash2, Image as ImageIcon, GripHorizontal, Move } from 'lucide-react';
import { ProductCard } from './ProductCard';
import { Language, Translations, Page, Product, Testimonial } from '../types';
import { EditableText } from './EditableText';
import { useEditor } from '../contexts/EditorContext';
import { ImageWithLoader } from './ImageWithLoader';
import { getOptimizedUrl } from '../utils/fileHelpers';
import { ImageEditorModal } from './ImageEditorModal';
import { SEOHead } from './SEOHead';

// dnd-kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface HomeProps {
  currentLang: Language;
  t: Translations[Language];
  setPage: (page: Page) => void;
  onProductClick?: (id: string) => void;
}

const SortableHomeProduct: React.FC<{
  product: Product;
  currentLang: Language;
  t: Translations[Language];
  onProductClick?: (id: string) => void;
}> = ({ product, currentLang, t, onProductClick }) => {
  const { isEditMode, toggleProductFeatured } = useEditor();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.7 : 1,
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style} className="h-full">
      {isEditMode && (
         <button
            {...attributes}
            {...listeners}
            className="absolute -top-3 left-1/2 -translate-x-1/2 z-[45] bg-white border border-gray-200 shadow-md p-1.5 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-50 text-gray-400 hover:text-primary-600 hover:scale-110 transition-all touch-none"
         >
            <GripHorizontal size={20} />
         </button>
      )}
      <ProductCard 
        product={product} 
        currentLang={currentLang} 
        t={t} 
        onViewDetails={onProductClick}
        variant="simple"
        onDelete={() => toggleProductFeatured(product.id)}
      />
    </div>
  );
};

const SortableTestimonialItem: React.FC<{
  item: Testimonial;
  currentLang: Language;
}> = ({ item, currentLang }) => {
  const { isEditMode, updateTestimonial, deleteTestimonial } = useEditor();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !isEditMode });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.7 : 1,
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style} className="snap-center h-auto flex-shrink-0 w-[220px] md:w-auto bg-white/5 backdrop-blur-md rounded-xl p-3 md:p-8 border border-white/5 hover:border-white/20 hover:bg-white/10 transition-all relative group flex flex-col min-w-[220px] md:min-w-0">
       {isEditMode && (
         <button
            {...attributes}
            {...listeners}
            className="absolute -top-3 left-1/2 -translate-x-1/2 z-[60] bg-white border border-gray-200 shadow-md p-1.5 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-50 text-gray-400 hover:text-primary-600 hover:scale-110 transition-all touch-none"
         >
            <GripHorizontal size={20} />
         </button>
       )}
       {isEditMode && (
           <button onClick={() => deleteTestimonial(item.id)} className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full opacity-50 hover:opacity-100 z-50 transition-all"><Trash2 size={14} /></button>
        )}
        <div className="absolute top-3 right-3 md:top-6 md:right-6 text-primary-500/20 group-hover:text-primary-500/40 transition-colors"><Quote size={20} className="rotate-180 md:w-10 md:h-10" fill="currentColor" /></div>
        <div className="flex gap-0.5 mb-2 md:mb-6 text-accent-500">{[...Array(item.rating)].map((_, i) => <Star key={i} size={10} className="md:w-[14px] md:h-[14px]" fill="currentColor" />)}</div>
        <div className="mb-4 md:mb-8 relative z-10 font-light text-xs md:text-lg">
          {isEditMode ? (
             <textarea className="w-full bg-slate-800/50 text-white border border-slate-600 rounded p-2 text-xs" rows={4} value={item.text[currentLang]} onChange={(e) => updateTestimonial(item.id, 'text', currentLang, e.target.value)} />
          ) : ( <p className="italic leading-relaxed text-slate-300 line-clamp-4 md:line-clamp-none">"{item.text[currentLang]}"</p> )}
        </div>
        <div className="flex items-center gap-2 md:gap-4 mt-auto pt-3 md:pt-6 border-t border-white/5">
          <div className="w-6 h-6 md:w-10 md:h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center font-bold text-white shadow-lg shrink-0 text-[10px] md:text-sm">{item.name.charAt(0)}</div>
          <div className="min-w-0 flex-1">
            {isEditMode ? (
               <div className="flex flex-col gap-1">
                  <input value={item.name} onChange={(e) => updateTestimonial(item.id, 'name', currentLang, e.target.value)} className="bg-slate-800 border border-slate-600 rounded px-1 text-xs font-bold w-full" />
                  <div className="flex gap-1"><input value={item.company} onChange={(e) => updateTestimonial(item.id, 'company', currentLang, e.target.value)} className="bg-slate-800 border border-slate-600 rounded px-1 text-[8px] w-1/2" /><input value={item.country} onChange={(e) => updateTestimonial(item.id, 'country', currentLang, e.target.value)} className="bg-slate-800 border border-slate-600 rounded px-1 text-[8px] w-1/2" /></div>
               </div>
            ) : ( <> <h4 className="font-bold text-white tracking-wide truncate text-[10px] md:text-sm">{item.name}</h4><p className="text-[9px] md:text-xs text-slate-400 font-medium truncate">{item.company}, {item.country}</p> </> )}
          </div>
        </div>
    </div>
  );
}

export const Home: React.FC<HomeProps> = ({ currentLang, t, setPage, onProductClick }) => {
  const isRTL = currentLang === Language.AR;
  const { products, isEditMode, updateTranslation, translations, testimonials, partners, updateTestimonial, addTestimonial, deleteTestimonial, reorderTestimonials, updatePartner, addPartner, deletePartner, reorderProducts } = useEditor();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Derive slides array from reactive translations object to ensure instant updates
  const slides = useMemo(() => [
    translations[currentLang]?.hero?.image1 || t.hero.image1,
    translations[currentLang]?.hero?.image2 || t.hero.image2,
    translations[currentLang]?.hero?.image3 || t.hero.image3
  ], [translations, currentLang, t.hero]);
  
  const [editorTarget, setEditorTarget] = useState<{ id: string, type: 'hero' | 'partner', initialUrl: string } | null>(null);
  const [loadedSlides, setLoadedSlides] = useState<number[]>([0]);

  const featuredProducts = useMemo(() => products.filter(p => p.featured !== false), [products]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadedSlides(prev => {
        const next = [...prev];
        let changed = false;
        if (!next.includes(currentSlide)) {
          next.push(currentSlide);
          changed = true;
        }
        const nextSlideIdx = (currentSlide + 1) % slides.length;
        if (!next.includes(nextSlideIdx)) {
          next.push(nextSlideIdx);
          changed = true;
        }
        return changed ? next : prev;
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [currentSlide, slides.length]);

  useEffect(() => {
    const timer = setInterval(() => { if (!isEditMode && !editorTarget) setCurrentSlide((prev) => (prev + 1) % slides.length); }, 6000); 
    return () => clearInterval(timer);
  }, [slides.length, isEditMode, editorTarget]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = products.findIndex((p) => p.id === active.id);
      const newIndex = products.findIndex((p) => p.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderProducts(arrayMove(products, oldIndex, newIndex));
      }
    }
  };

  const handleTestimonialDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = testimonials.findIndex((t) => t.id === active.id);
      const newIndex = testimonials.findIndex((t) => t.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) reorderTestimonials(arrayMove(testimonials, oldIndex, newIndex));
    }
  };

  return (
    <>
      <SEOHead 
        title="Coriolis Mass Flowmeters & Industrial Instrumentation" 
        description="MEITAI Instruments is a leading manufacturer of high-precision Coriolis Mass Flowmeters and advanced industrial instrumentation for global process control and automation."
        keywords="Coriolis Mass Flowmeters, Industrial Instrumentation, flowmeters, pressure transmitters, MEITAI, process control"
      />
      <ImageEditorModal 
        isOpen={!!editorTarget}
        onClose={() => setEditorTarget(null)}
        initialUrl={editorTarget?.initialUrl || ''}
        initialScale={editorTarget?.type === 'hero' ? (translations[currentLang]?.hero?.[`imageScale${currentSlide+1}`] || 1) : 1}
        initialPosition={editorTarget?.type === 'hero' ? (translations[currentLang]?.hero?.[`imagePosition${currentSlide+1}`] || '50% 50%') : '50% 50%'}
        title={editorTarget?.type === 'hero' ? `Edit Slide ${currentSlide+1}` : 'Edit Partner Logo'}
        aspectRatio={editorTarget?.type === 'hero' ? 'banner' : 'square'}
        onSave={async (data) => {
          if (editorTarget?.type === 'hero') {
            updateTranslation(currentLang, `hero.image${currentSlide + 1}`, data.url);
            updateTranslation(currentLang, `hero.imageScale${currentSlide + 1}`, data.scale.toString());
            updateTranslation(currentLang, `hero.imagePosition${currentSlide + 1}`, data.position);
          } else if (editorTarget?.type === 'partner') {
            const p = partners.find(x => x.id === editorTarget.id);
            updatePartner(editorTarget.id, p?.name || 'Partner', data.url);
          }
        }}
      />

      <section className="relative h-[65vh] md:h-[80vh] min-h-[500px] overflow-hidden bg-secondary-900 group">
        {slides.map((img, index) => {
          const slideScale = translations[currentLang]?.hero?.[`imageScale${index + 1}`] || 1;
          const slidePos = translations[currentLang]?.hero?.[`imagePosition${index + 1}`] || '50% 50%';
          const isLoaded = loadedSlides.includes(index);
          return (
            <div key={index} className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${index === currentSlide ? 'opacity-100 scale-100 z-0' : 'opacity-0 scale-105 -z-10'}`}>
               <div className="absolute inset-0 bg-gradient-to-t from-secondary-900 via-secondary-900/40 to-transparent md:bg-gradient-to-r md:via-secondary-900/60 z-10" /> 
               {isLoaded && <ImageWithLoader src={img} alt={`High-Precision Coriolis Mass Flowmeter Banner ${index + 1}`} priority={index === 0} className="w-full h-full object-cover" containerClassName="w-full h-full" scale={slideScale} style={{ objectPosition: slidePos }} />}
            </div>
          );
        })}

        <div className="relative z-20 h-full flex items-end md:items-center pb-20 md:pb-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl text-white pt-10 animate-in slide-in-from-left-10 fade-in duration-1000">
              <div className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-primary-600/30 border border-primary-500/50 text-white text-[10px] md:text-xs font-bold uppercase tracking-widest mb-4 md:mb-8 backdrop-blur-md shadow-lg">
                <Globe2 size={12} className="text-primary-400" /> <EditableText path="common.globalLeader" lang={currentLang} />
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold mb-4 md:mb-8 leading-[1.1] md:leading-[0.9] font-display tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-300">
                <EditableText path="hero.title" lang={currentLang} />
              </h1>
              <div className="text-base sm:text-lg md:text-2xl text-slate-300 mb-8 md:mb-12 max-w-2xl leading-relaxed font-light"><EditableText path="hero.subtitle" lang={currentLang} multiline={true} /></div>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => setPage('products')} className="bg-primary-600 hover:bg-primary-500 text-white px-8 md:px-10 py-3 md:py-4 rounded-full font-bold text-sm transition-all shadow-xl flex items-center gap-2 group/btn"><EditableText path="hero.cta" lang={currentLang} /><span className="bg-white/20 rounded-full p-1 group-hover/btn:bg-white/30 transition-colors">{isRTL ? <ChevronRight className="rotate-180" size={16}/> : <ChevronRight size={16}/>}</span></button>
                <button onClick={() => setPage('contact')} className="bg-white/5 backdrop-blur-sm border border-white/20 hover:bg-white/10 text-white px-8 md:px-10 py-3 md:py-4 rounded-full font-bold text-sm transition-all"> <EditableText path="products.inquire" lang={currentLang} /> </button>
              </div>
            </div>
          </div>
        </div>

        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button onClick={prevSlide} className="absolute left-6 top-1/2 -translate-y-1/2 z-30 p-4 text-white/70 hover:text-white bg-black/20 hover:bg-black/50 backdrop-blur-md rounded-full transition-all hidden md:block border border-white/10"><ChevronLeft size={28} /></button>
            <button onClick={nextSlide} className="absolute right-6 top-1/2 -translate-y-1/2 z-30 p-4 text-white/70 hover:text-white bg-black/20 hover:bg-black/50 backdrop-blur-md rounded-full transition-all hidden md:block border border-white/10"><ChevronRight size={28} /></button>
        </div>

        <div className="absolute bottom-12 md:bottom-32 left-1/2 -translate-x-1/2 z-30 flex gap-4">
          {slides.map((_, idx) => ( <button key={idx} onClick={() => setCurrentSlide(idx)} className={`h-1.5 rounded-full transition-all duration-500 ${idx === currentSlide ? 'bg-primary-500 w-16 shadow-[0_0_10px_rgba(12,135,235,0.8)]' : 'bg-white/20 w-8 hover:bg-white/50'}`} /> ))}
        </div>

        {isEditMode && (
          <div className="absolute top-24 right-6 z-40">
            <button onClick={() => setEditorTarget({ id: `hero-${currentSlide}`, type: 'hero', initialUrl: slides[currentSlide] })} className="bg-white text-slate-900 px-4 py-2 rounded-xl shadow-xl font-bold text-sm flex items-center gap-2 hover:bg-slate-50 transition-all"> <Move size={16} /> Adjust Banner {currentSlide + 1} </button>
          </div>
        )}
      </section>

      <section className="relative z-30 -mt-10 md:-mt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
             {[ { icon: Globe, path: 'stats.countVal', label: 'stats.countLabel', border: 'border-primary-500' }, { icon: Factory, path: 'stats.isoVal', label: 'stats.isoLabel', border: 'border-accent-500' }, { icon: MapPin, path: 'stats.hubsVal', label: 'stats.hubsLabel', border: 'border-primary-500' }, { icon: Star, path: 'stats.clientsVal', label: 'stats.clientsLabel', border: 'border-accent-500' } ].map((item, idx) => (
                <div key={idx} className={`bg-white rounded-xl shadow-lg p-4 md:p-6 flex flex-col items-center text-center border-t-4 ${item.border} group hover:-translate-y-1 transition-transform`}>
                    <div className="p-2 md:p-3 bg-slate-50 rounded-full text-slate-600 mb-2 md:mb-3 group-hover:scale-110 transition-transform"><item.icon size={20} className="md:w-6 md:h-6"/></div>
                    <div><p className="font-display font-bold text-slate-900 text-2xl md:text-3xl"><EditableText path={item.path} lang={currentLang} /></p><p className="text-[10px] md:text-xs text-slate-500 font-bold uppercase tracking-wider mt-1"><EditableText path={item.label} lang={currentLang} /></p></div>
                </div>
             ))}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="lg:hidden flex flex-col gap-6">
              <div className="text-center mb-8"><h2 className="text-3xl font-black uppercase leading-tight font-display mb-2 text-slate-900"><EditableText path="whyChooseUs.title" lang={currentLang} /></h2><p className="text-lg text-slate-500 font-medium"><EditableText path="whyChooseUs.subtitle" lang={currentLang} /></p></div>
              <div className="flex overflow-x-auto gap-4 pb-8 snap-x hide-scrollbar">
                  {[ { key: 'strength', num: '01', bg: 'from-[#7B92B8] to-[#97ADD6]' }, { key: 'inspection', num: '02', bg: 'bg-[#859ABF]' }, { key: 'quality', num: '03', bg: 'bg-[#8EA3C6]' }, { key: 'application', num: '04', bg: 'bg-[#97ADD6]' }, { key: 'service', num: '05', bg: 'bg-[#A0B5DB]' } ].map(item => (
                      <div key={item.key} className={`min-w-[85vw] md:min-w-[70vw] snap-center p-8 text-white rounded-3xl relative overflow-hidden shadow-xl bg-gradient-to-r ${item.bg}`}>
                          <h3 className="text-2xl font-bold mb-3"><EditableText path={`whyChooseUs.items.${item.key}.title`} lang={currentLang} /></h3>
                          <div className="opacity-90 leading-relaxed font-light mb-6"><EditableText path={`whyChooseUs.items.${item.key}.desc`} lang={currentLang} multiline /></div>
                          <span className="text-6xl font-display font-bold opacity-20 block text-right">{item.num}</span>
                      </div>
                  ))}
              </div>
           </div>
           
           {/* REORDERED GRID: FEATURE BLOCK FIRST, TITLE BLOCK LAST (on Desktop) */}
           <div className="hidden lg:grid grid-cols-1 lg:grid-cols-4 shadow-2xl rounded-3xl overflow-hidden">
               {/* 1. Feature 01 Block (Now positioned first) */}
               <div className="lg:col-span-3 bg-gradient-to-r from-[#7B92B8] to-[#97ADD6] p-10 text-white flex flex-col justify-center relative overflow-hidden">
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold mb-4 relative pl-4 border-l-4 border-white/30"><EditableText path="whyChooseUs.items.strength.title" lang={currentLang} /></h3>
                    <div className="opacity-90 leading-relaxed max-w-2xl text-lg font-light mb-6"><EditableText path="whyChooseUs.items.strength.desc" lang={currentLang} multiline /></div>
                    <span className="text-8xl font-display font-bold opacity-10 absolute bottom-0 right-0 pointer-events-none translate-y-1/4">01</span>
                  </div>
               </div>

               {/* 2. Main Title Block (Now positioned last in row 1) */}
               <div className="lg:col-span-1 bg-[#8CC63F] p-10 text-white flex flex-col justify-center min-h-[300px]">
                  <h2 className="text-4xl font-black uppercase leading-tight mb-2 font-display"><EditableText path="whyChooseUs.title" lang={currentLang} /></h2>
                  <div className="w-12 h-1 bg-white/40 mb-4 rounded-full"></div>
                  <p className="text-xl opacity-90 font-medium"><EditableText path="whyChooseUs.subtitle" lang={currentLang} /></p>
               </div>

               {/* 3. Small Feature Blocks (02-05) */}
               {[ { key: 'inspection', num: '02', bg: 'bg-[#859ABF]' }, { key: 'quality', num: '03', bg: 'bg-[#8EA3C6]' }, { key: 'application', num: '04', bg: 'bg-[#97ADD6]' }, { key: 'service', num: '05', bg: 'bg-[#A0B5DB]' } ].map(item => (
                   <div key={item.key} className={`lg:col-span-1 ${item.bg} p-8 text-white border-r border-white/10 relative overflow-hidden group hover:opacity-90 transition-opacity flex flex-col`}>
                      <h3 className="text-xl font-bold mb-3"><EditableText path={`whyChooseUs.items.${item.key}.title`} lang={currentLang} /></h3>
                      <div className="text-sm opacity-80 leading-relaxed flex-1"><EditableText path={`whyChooseUs.items.${item.key}.desc`} lang={currentLang} multiline /></div>
                      <span className="text-6xl font-display font-bold opacity-10 mt-6 block text-right group-hover:opacity-20 transition-opacity">{item.num}</span>
                   </div>
               ))}
           </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16"><span className="text-primary-600 font-bold uppercase tracking-[0.2em] text-xs mb-3 block"><EditableText path="common.catalogLabel" lang={currentLang} /></span><h2 className="text-3xl font-display font-bold text-slate-900 sm:text-5xl"><EditableText path="products.title" lang={currentLang} /></h2><div className="w-20 h-1.5 bg-primary-600 mx-auto mt-4 md:mt-6 rounded-full"></div></div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={featuredProducts.map(p => p.id)} strategy={rectSortingStrategy} disabled={!isEditMode}>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8">
                {featuredProducts.length > 0 ? featuredProducts.map(product => ( <SortableHomeProduct key={product.id} product={product} currentLang={currentLang} t={t} onProductClick={onProductClick} /> )) : ( <div className="col-span-2 lg:col-span-4 w-full text-center py-10 text-gray-400 italic bg-white rounded-xl border border-dashed border-gray-300"> No featured products selected. </div> )}
              </div>
            </SortableContext>
          </DndContext>
          <div className="mt-8 md:mt-16 text-center"><button onClick={() => setPage('products')} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white border border-slate-200 text-slate-700 font-bold rounded-full hover:border-primary-600 hover:text-primary-600 transition-all shadow-sm"> <EditableText path="common.viewAll" lang={currentLang} /> {isRTL ? <ChevronRight className="rotate-180" size={18}/> : <ChevronRight size={18}/>} </button></div>
        </div>
      </section>

      <section className="py-8 md:py-16 bg-white border-t border-slate-100">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-center text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px] md:text-xs mb-6 md:mb-12"><EditableText path="home.partnersTitle" lang={currentLang} /></h3>
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-6 md:gap-x-12 md:gap-y-12 grayscale hover:grayscale-0 transition-all duration-500">
               {partners.map(partner => (
                  <div key={partner.id} className="group flex flex-col items-center gap-2.5 cursor-default relative">
                     {isEditMode && ( <div className="absolute -top-3 -right-3 flex gap-1 z-20"><button onClick={() => deletePartner(partner.id)} className="bg-red-500 text-white rounded-full p-1 shadow-sm hover:bg-red-600"><Trash2 size={12} /></button></div> )}
                     <div className="relative w-16 h-8 md:w-28 md:h-16 flex items-center justify-center group-hover:scale-110 transition-transform">
                        {partner.logoUrl.startsWith('http') ? ( <img src={getOptimizedUrl(partner.logoUrl || '') || undefined} alt={`${partner.name} - Industrial Partner`} className="w-full h-full object-contain" /> ) : ( <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-100 rounded-full flex items-center justify-center text-[10px] md:text-sm font-bold text-slate-400">{partner.logoUrl}</div> )}
                        {isEditMode && ( <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer" onClick={() => setEditorTarget({ id: partner.id, type: 'partner', initialUrl: partner.logoUrl })}> <ImageIcon size={16} className="text-white" /> </div> )}
                     </div>
                     {isEditMode && ( <input type="text" value={partner.name} onChange={(e) => updatePartner(partner.id, e.target.value, partner.logoUrl)} className="bg-slate-50 border border-slate-200 rounded px-1 py-0.5 text-[10px] text-center w-24" /> )}
                  </div>
               ))}
               {isEditMode && ( <button onClick={addPartner} className="flex flex-col items-center gap-2 text-primary-500 hover:text-primary-700"><div className="w-12 h-12 md:w-16 md:h-16 border-2 border-dashed border-primary-300 rounded-lg flex items-center justify-center"><Plus size={20} /></div><span className="text-[10px] font-bold">Add Partner</span></button> )}
            </div>
         </div>
      </section>

      <section className="py-8 md:py-24 bg-secondary-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 md:mb-20"><span className="text-accent-500 font-bold uppercase tracking-[0.2em] text-xs mb-3 block"><EditableText path="common.successStories" lang={currentLang} /></span><h2 className="text-3xl font-display font-bold sm:text-4xl text-white"><EditableText path="home.testimonialsTitle" lang={currentLang} /></h2></div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTestimonialDragEnd}>
            <SortableContext items={testimonials.map(t => t.id)} strategy={rectSortingStrategy} disabled={!isEditMode}>
              <div className="flex overflow-x-auto gap-4 md:gap-6 pb-4 -mx-4 px-4 snap-x hide-scrollbar md:grid md:grid-cols-3 md:gap-8 md:overflow-visible">
                {testimonials.map(item => ( <SortableTestimonialItem key={item.id} item={item} currentLang={currentLang} /> ))}
                {isEditMode && ( <div className="snap-center flex-shrink-0 w-[220px] md:w-auto"><button onClick={addTestimonial} className="w-full h-full bg-white/5 backdrop-blur-md rounded-2xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center min-h-[200px] md:min-h-[300px] hover:bg-white/10 hover:border-accent-500/50 transition-all group"><div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center mb-4 group-hover:bg-accent-500 transition-colors"><Plus size={28} /></div><span className="font-bold text-slate-400 group-hover:text-white">Add Testimonial</span></button></div> )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </section>
    </>
  );
};
